import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import type { MedVisitBulkUpdatePatch } from "@/types/medchron";
import { formatDate } from "@/utils/dateUtils";

import type { MedChronTemplate, VisitDisplay } from "../types";
import type {
  BulkEditField,
  BulkEditHeaderValues,
  BulkEditSaveValues,
  BulkEditTextField,
} from "../types/bulkEdit";
import { areCitationListsEqual, toCitationDraftRows } from "./visitCitations";
import { formatCitationPages, formatVisitType } from "./visitFormatting";

export interface BulkEditPreviewSummaryRow {
  field: BulkEditField;
  label: string;
  fromLabel: string;
  toLabel: string;
  visitsAffected: number;
}

export interface BulkEditPreviewVisitRow {
  field: BulkEditField;
  label: string;
  fromLabel: string;
  toLabel: string;
}

export interface BulkEditPreviewVisit {
  visitId: string;
  label: string;
  fields: BulkEditPreviewVisitRow[];
}

export interface BulkEditPreview {
  visitCount: number;
  replacesCitations: boolean;
  summaryRows: BulkEditPreviewSummaryRow[];
  visits: BulkEditPreviewVisit[];
}

const BULK_EDIT_TEXT_FIELD_ORDER: BulkEditTextField[] = [
  "provider_name",
  "facility_name",
  "visit_subtype",
  "visit_type_id",
];

const BULK_EDIT_FIELD_LABELS: Record<BulkEditField, string> = {
  visit_type_id: "Visit type",
  provider_name: "Provider",
  facility_name: "Facility",
  visit_subtype: "Subtype",
  citations: "Citations",
};

function normalizeHeaderField(value: string | null | undefined): string {
  return value ?? "";
}

function collectVisitsById(visits: VisitDisplay[]): Map<string, VisitDisplay> {
  const byId = new Map<string, VisitDisplay>();
  for (const visit of visits) {
    if (visit.allVisits?.length) {
      for (const child of visit.allVisits) {
        byId.set(child.visit_id, child);
      }
      continue;
    }
    byId.set(visit.visit_id, visit);
  }
  return byId;
}

function getSelectedVisits(
  selectedVisitIds: Iterable<string>,
  visits: VisitDisplay[]
): VisitDisplay[] {
  const byId = collectVisitsById(visits);
  return [...selectedVisitIds]
    .map((visitId) => byId.get(visitId))
    .filter((visit): visit is VisitDisplay => visit !== undefined);
}

function sortVisitsForPreview(visits: VisitDisplay[]): VisitDisplay[] {
  return [...visits].sort((left, right) => {
    const leftDate = left.visit_date ?? "";
    const rightDate = right.visit_date ?? "";
    if (leftDate !== rightDate) {
      return leftDate.localeCompare(rightDate);
    }
    return left.visit_id.localeCompare(right.visit_id);
  });
}

function unifiedHeaderFieldValue(selectedVisits: VisitDisplay[], field: BulkEditTextField): string {
  if (selectedVisits.length === 0) {
    return "";
  }

  const sharedValue = normalizeHeaderField(selectedVisits[0][field]);
  for (let index = 1; index < selectedVisits.length; index += 1) {
    if (normalizeHeaderField(selectedVisits[index][field]) !== sharedValue) {
      return "";
    }
  }
  return sharedValue;
}

function formatVisitTypeLabel(visitTypeId: string, template: MedChronTemplate | null): string {
  if (!visitTypeId) {
    return "(Empty)";
  }
  return template?.visitTypes?.[visitTypeId]?.name ?? visitTypeId;
}

function formatCitationListLabel(
  citations: MedVisitCitation[],
  fileMapping: Record<string, string>,
  shortenFileName: (name: string) => string
): string {
  if (citations.length === 0) {
    return "(Empty)";
  }
  return citations
    .map(
      (citation) =>
        `${shortenFileName(fileMapping[citation.file_id] ?? "Unknown file")} ${formatCitationPages(citation)}`
    )
    .join("; ");
}

function formatFieldLabel(
  field: BulkEditTextField,
  value: string,
  template: MedChronTemplate | null
): string {
  if (field === "visit_type_id") {
    return formatVisitTypeLabel(value, template);
  }
  return value.trim() ? value.trim() : "(Empty)";
}

function formatFromFieldLabel(
  field: BulkEditTextField,
  selectedVisits: VisitDisplay[],
  template: MedChronTemplate | null
): string {
  const sharedValue = unifiedHeaderFieldValue(selectedVisits, field);
  if (!sharedValue && selectedVisits.length > 1) {
    const firstValue = normalizeHeaderField(selectedVisits[0][field]);
    for (let index = 1; index < selectedVisits.length; index += 1) {
      if (normalizeHeaderField(selectedVisits[index][field]) !== firstValue) {
        return "Multiple values";
      }
    }
  }
  return formatFieldLabel(field, sharedValue, template);
}

function fieldHasEffectiveChange(
  selectedVisits: VisitDisplay[],
  field: BulkEditTextField,
  newValue: string
): boolean {
  const normalizedNewValue = normalizeHeaderField(newValue.trim());
  if (!normalizedNewValue) {
    return false;
  }
  return selectedVisits.some((visit) => normalizeHeaderField(visit[field]) !== normalizedNewValue);
}

function countVisitsAffected(
  selectedVisits: VisitDisplay[],
  field: BulkEditTextField,
  newValue: string
): number {
  const normalizedNewValue = normalizeHeaderField(newValue.trim());
  return selectedVisits.filter((visit) => normalizeHeaderField(visit[field]) !== normalizedNewValue)
    .length;
}

function unifiedCitationList(selectedVisits: VisitDisplay[]): MedVisitCitation[] | null {
  const [first] = selectedVisits;
  if (!first) {
    return null;
  }
  return selectedVisits.every((visit) => areCitationListsEqual(visit.citations, first.citations))
    ? first.citations
    : null;
}

function visitsWithChangedCitations(
  selectedVisits: VisitDisplay[],
  citations: MedVisitCitation[]
): VisitDisplay[] {
  if (citations.length === 0) {
    return [];
  }
  return selectedVisits.filter((visit) => !areCitationListsEqual(visit.citations, citations));
}

function buildVisitFieldRow(
  visit: VisitDisplay,
  field: BulkEditTextField,
  newValue: string,
  template: MedChronTemplate | null
): BulkEditPreviewVisitRow | null {
  const trimmedNewValue = newValue.trim();
  if (!trimmedNewValue || !fieldHasEffectiveChange([visit], field, trimmedNewValue)) {
    return null;
  }

  return {
    field,
    label: BULK_EDIT_FIELD_LABELS[field],
    fromLabel: formatFieldLabel(field, normalizeHeaderField(visit[field]), template),
    toLabel: formatFieldLabel(field, trimmedNewValue, template),
  };
}

function buildSummaryRow(
  selectedVisits: VisitDisplay[],
  field: BulkEditTextField,
  values: BulkEditSaveValues,
  template: MedChronTemplate | null
): BulkEditPreviewSummaryRow | null {
  const newValue = values[field].trim();
  if (!fieldHasEffectiveChange(selectedVisits, field, newValue)) {
    return null;
  }

  return {
    field,
    label: BULK_EDIT_FIELD_LABELS[field],
    fromLabel: formatFromFieldLabel(field, selectedVisits, template),
    toLabel: formatFieldLabel(field, newValue, template),
    visitsAffected: countVisitsAffected(selectedVisits, field, newValue),
  };
}

function buildCitationsSummaryRow(
  selectedVisits: VisitDisplay[],
  citations: MedVisitCitation[],
  fileMapping: Record<string, string>,
  shortenFileName: (name: string) => string
): BulkEditPreviewSummaryRow | null {
  const changedVisits = visitsWithChangedCitations(selectedVisits, citations);
  if (changedVisits.length === 0) {
    return null;
  }
  const sharedCitations = unifiedCitationList(selectedVisits);
  return {
    field: "citations",
    label: BULK_EDIT_FIELD_LABELS.citations,
    fromLabel: sharedCitations
      ? formatCitationListLabel(sharedCitations, fileMapping, shortenFileName)
      : "Multiple values",
    toLabel: formatCitationListLabel(citations, fileMapping, shortenFileName),
    visitsAffected: changedVisits.length,
  };
}

export function formatBulkEditVisitHeaderLabel(
  visit: VisitDisplay,
  values: BulkEditSaveValues,
  template: MedChronTemplate | null
): string {
  const visitTypeId = values.visit_type_id.trim() || visit.visit_type_id;
  const providerName = values.provider_name.trim() || visit.provider_name || "Unknown";
  const facilityName = values.facility_name.trim() || visit.facility_name;
  const dateLabel = visit.visit_date ? formatDate(visit.visit_date) : "Unknown date";
  const visitTypeLabel = formatVisitType(template, visitTypeId);
  const baseLabel = `${dateLabel}, ${visitTypeLabel}, ${providerName}`;

  return facilityName ? `${baseLabel} (${facilityName})` : baseLabel;
}

export function buildBulkEditHeaderPrefill(
  selectedVisitIds: Iterable<string>,
  visits: VisitDisplay[]
): BulkEditHeaderValues {
  const selectedVisits = getSelectedVisits(selectedVisitIds, visits);

  return {
    visit_type_id: unifiedHeaderFieldValue(selectedVisits, "visit_type_id"),
    provider_name: unifiedHeaderFieldValue(selectedVisits, "provider_name"),
    facility_name: unifiedHeaderFieldValue(selectedVisits, "facility_name"),
    visit_subtype: unifiedHeaderFieldValue(selectedVisits, "visit_subtype"),
    citations: toCitationDraftRows(unifiedCitationList(selectedVisits) ?? []),
  };
}

export function buildBulkEditPreview(
  selectedVisitIds: Iterable<string>,
  visits: VisitDisplay[],
  values: BulkEditSaveValues,
  template: MedChronTemplate | null,
  fileMapping: Record<string, string>,
  shortenFileName: (name: string) => string
): BulkEditPreview | null {
  const selectedVisits = getSelectedVisits(selectedVisitIds, visits);
  const textRows = BULK_EDIT_TEXT_FIELD_ORDER.flatMap((field) => {
    const row = buildSummaryRow(selectedVisits, field, values, template);
    return row ? [row] : [];
  });
  const citationsRow = buildCitationsSummaryRow(
    selectedVisits,
    values.citations,
    fileMapping,
    shortenFileName
  );
  const summaryRows = citationsRow ? [...textRows, citationsRow] : textRows;

  if (summaryRows.length === 0) {
    return null;
  }

  const sortedVisits = sortVisitsForPreview(selectedVisits);

  return {
    visitCount: selectedVisits.length,
    replacesCitations: citationsRow !== null,
    summaryRows,
    visits: sortedVisits.flatMap((visit) => {
      const fields = BULK_EDIT_TEXT_FIELD_ORDER.flatMap((field) => {
        const row = buildVisitFieldRow(visit, field, values[field], template);
        return row ? [row] : [];
      });
      if (visitsWithChangedCitations([visit], values.citations).length > 0) {
        fields.push({
          field: "citations",
          label: BULK_EDIT_FIELD_LABELS.citations,
          fromLabel: formatCitationListLabel(visit.citations, fileMapping, shortenFileName),
          toLabel: formatCitationListLabel(values.citations, fileMapping, shortenFileName),
        });
      }

      if (fields.length === 0) {
        return [];
      }

      return [
        {
          visitId: visit.visit_id,
          label: formatBulkEditVisitHeaderLabel(visit, values, template),
          fields,
        },
      ];
    }),
  };
}

export function buildEffectiveBulkEditValues(
  selectedVisitIds: Iterable<string>,
  visits: VisitDisplay[],
  values: BulkEditSaveValues
): BulkEditSaveValues {
  const selectedVisits = getSelectedVisits(selectedVisitIds, visits);
  return {
    visit_type_id: fieldHasEffectiveChange(selectedVisits, "visit_type_id", values.visit_type_id)
      ? values.visit_type_id.trim()
      : "",
    provider_name: fieldHasEffectiveChange(selectedVisits, "provider_name", values.provider_name)
      ? values.provider_name.trim()
      : "",
    facility_name: fieldHasEffectiveChange(selectedVisits, "facility_name", values.facility_name)
      ? values.facility_name.trim()
      : "",
    visit_subtype: fieldHasEffectiveChange(selectedVisits, "visit_subtype", values.visit_subtype)
      ? values.visit_subtype.trim()
      : "",
    citations:
      visitsWithChangedCitations(selectedVisits, values.citations).length > 0
        ? values.citations
        : [],
  };
}

export function buildBulkVisitUpdatePayload(
  values: BulkEditSaveValues
): MedVisitBulkUpdatePatch | null {
  const patch: MedVisitBulkUpdatePatch = {};

  const visitTypeId = values.visit_type_id.trim();
  if (visitTypeId) {
    patch.visit_type_id = visitTypeId;
  }

  const providerName = values.provider_name.trim();
  if (providerName) {
    patch.provider_name = providerName;
  }

  const facilityName = values.facility_name.trim();
  if (facilityName) {
    patch.facility_name = facilityName;
  }

  const visitSubtype = values.visit_subtype.trim();
  if (visitSubtype) {
    patch.visit_subtype = visitSubtype;
  }

  if (values.citations.length > 0) {
    patch.citations = values.citations;
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
